import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Save, Copy } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { setSiteCache } from "@/lib/card";
import { siteHome } from "@/lib/siteThemes";
import DesignTab from "@/components/website/DesignTab";
import ContentTab from "@/components/website/ContentTab";
import ProductsTab from "@/components/website/ProductsTab";
import EnquiriesTab from "@/components/website/EnquiriesTab";

const TABS = [["design", "Theme & link"], ["content", "Content & gold rate"], ["products", "Products"], ["enquiries", "Enquiries"]];

export default function WebsitePage() {
  const [site, setSite] = useState(null);
  const [tab, setTab] = useState("design");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const load = useCallback(() => api.get("/site").then((r) => setSite(r.data)), []);
  useEffect(() => { load(); api.get("/enquiries").then((r) => setUnread(r.data.filter((e) => !e.read).length)); }, [load]);
  const set = (patch) => setSite((s) => ({ ...s, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/site", site);
      setSite(data);
      setSiteCache(data);
      toast.success("Website saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const url = site ? `${window.location.origin}/store/${site.slug}` : "";
  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">Showroom website</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Your website</h1>
            {site && (
              <div className="flex items-center gap-2 mt-3 text-sm">
                <span className="font-mono text-slate-300" data-testid="website-url">{site.domain ? `https://${site.domain}` : url}</span>
                <button onClick={() => navigator.clipboard.writeText(siteHome(site)).then(() => toast.success("Link copied"), () => toast.error("Copy blocked"))} className="p-1 text-slate-400 hover:text-white" data-testid="website-copy-url"><Copy size={13} /></button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {site && <a href={url} target="_blank" rel="noreferrer" className="cf-btn-ghost h-10 text-sm px-4" data-testid="website-view-button"><ExternalLink size={14} /> View website</a>}
            <button onClick={save} disabled={busy || !site} className="cf-btn-primary h-10 text-sm px-5" data-testid="website-save-button">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
          </div>
        </div>
        <div className="flex gap-1 border-b border-slate-800" data-testid="website-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`website-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-amber-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
              {label}{k === "enquiries" && unread > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-amber-400 text-slate-950 text-[10px]">{unread}</span>}
            </button>
          ))}
        </div>
        {!site ? <p className="text-slate-500 text-sm">Loading…</p> : (
          <>
            {tab === "design" && <DesignTab site={site} set={set} />}
            {tab === "content" && <ContentTab site={site} set={set} />}
            {tab === "products" && <ProductsTab />}
            {tab === "enquiries" && <EnquiriesTab onRead={() => setUnread((n) => Math.max(0, n - 1))} />}
          </>
        )}
      </main>
    </div>
  );
}
